import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getTrans, getTransWithParams } from 'modules/i18n';
import { signalDialValue, signalMono, signalSlider } from '../signalStyles';
import { BlockHeading } from './BlockHeading';
import {
  legalValues,
  positionOf,
  readMagnitudeDialAnswer,
  readMagnitudeDialConfig,
  valueAt
} from './magnitudeDial';
import { BlockRendererProps, questionIdOf } from './types';

/** How many positions a continuous drag is quantised into. Fine enough to feel smooth, coarse enough
 *  that a log scale does not produce a different value for every pixel. */
const CONTINUOUS_STEPS = 1000;

/**
 * `magnitude_dial@1`: one quantity across a range too wide to step through.
 *
 * NOT A NUMBER STEPPER WITH A DIFFERENT SKIN, and the difference is the range. A stepper is for five to
 * forty hours, nudged one at a time. A dial is for fifty to fifty thousand, where stepping is absurd and
 * the question becomes "how do I get from here to a value three orders of magnitude away" — which is
 * what the scale is for.
 *
 * DRIVEN BY AN INDEX, not by the value. Every position the handle can reach has to be a value the server
 * accepts; a slider that produced 1234.5678 on a question whose step is 100 would look like it worked and
 * be refused at submit. So where the legal values can be enumerated the dial moves over that list, and
 * where they cannot it quantises the drag and rounds onto the step.
 *
 * **Untouched shows no number**, like the stepper, and for the same two reasons: displaying the minimum
 * before anyone has acted leaves a required question with Next disabled beneath a control that looks
 * answered, and writing that value on mount to fix it would record an answer nobody gave.
 */
export const MagnitudeDialBlock = ({
  block,
  answer,
  onChange,
  locale
}: BlockRendererProps) => {
  const config = useMemo(() => readMagnitudeDialConfig(block), [block]);
  const values = useMemo(() => legalValues(config), [config]);
  const value = readMagnitudeDialAnswer(answer)?.value ?? null;

  const questionId = questionIdOf(block);
  const unit = block.semantic?.unit ?? null;

  /*
    Where the handle sits before anyone has touched it.

    The low end, not the middle: a dial that opens halfway suggests a default nobody chose, and the
    respondent has to move it to answer either way. The value stays null until they do.
  */
  const sliderPosition = (): number => {
    if (values) {
      const index = value === null ? 0 : values.indexOf(nearest(values, value));
      return index < 0 ? 0 : index;
    }
    return value === null ? 0 : positionOf(value, config) * CONTINUOUS_STEPS;
  };

  const handle = (next: number) =>
    onChange({
      value: values
        ? values[Math.min(values.length - 1, Math.max(0, Math.round(next)))]
        : valueAt(next / CONTINUOUS_STEPS, config)
    });

  return (
    <Stack spacing={3}>
      {/* No derived hint: the ends of the range are printed under the track, where they sit beside
          the handle that travels between them. */}
      <BlockHeading block={block} locale={locale} />

      <Stack spacing={1.75}>
        {/* The value and its unit on one baseline, so "2.500" and "€" read as one phrase rather than a
            number with a label stuck under it. */}
        <Stack direction="row" alignItems="baseline" spacing={1.25}>
          <Typography
            component="div"
            data-testid="form-dial-value"
            aria-live="polite"
            sx={signalDialValue}
          >
            {value === null ? '—' : format(value, locale)}
          </Typography>
          {unit && (
            <Typography sx={{ fontSize: 14, color: 'text.disabled' }}>
              {unit}
            </Typography>
          )}
        </Stack>

        <Slider
          value={sliderPosition()}
          min={0}
          max={values ? values.length - 1 : CONTINUOUS_STEPS}
          step={1}
          onChange={(_event, next) =>
            handle(Array.isArray(next) ? next[0] : next)
          }
          aria-labelledby={questionId}
          /*
            The slider reports the VALUE to assistive tech, not the index it is driven by. Without
            these a screen reader announces "3 of 40", which is a position on a control rather than an
            answer to the question.
          */
          aria-valuetext={
            value === null
              ? getTrans('guestForm.dial.empty')
              : `${format(value, locale)}${unit ? ` ${unit}` : ''}`
          }
          data-testid="form-dial-slider"
          sx={signalSlider}
        />

        {/* The ends of the range, which the handle's position alone does not tell you. */}
        <Stack direction="row" justifyContent="space-between">
          <Typography
            sx={{ ...signalMono, fontSize: 12, color: 'text.disabled' }}
          >
            {format(config.min, locale)}
          </Typography>
          <Typography
            sx={{ ...signalMono, fontSize: 12, color: 'text.disabled' }}
          >
            {format(config.max, locale)}
          </Typography>
        </Stack>

        {config.scale === 'LOG' && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              {getTransWithParams('guestForm.dial.logScale', {
                min: config.min,
                max: config.max
              })}
            </Typography>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

/** The legal value closest to one that arrived from elsewhere — a resumed answer, or a config change. */
const nearest = (values: number[], value: number): number =>
  values.reduce((best, candidate) =>
    Math.abs(candidate - value) < Math.abs(best - value) ? candidate : best
  );

/**
 * A number as the respondent's locale writes it.
 *
 * Grouping matters more here than anywhere else in the flow: the whole point of the type is a range wide
 * enough that "50000" and "5000" are hard to tell apart at a glance.
 */
const format = (value: number, locale: string): string =>
  value.toLocaleString(locale, { maximumFractionDigits: 4 });
